/**
 * Selection name → Korean label resolver
 * 공용: 국내형/스페셜/해외형 모두 사용
 * 기본 매핑 + 조합형(And) 규칙 + match/market 문맥 기반 팀명 치환
 */

// 기본 단일 토큰 매핑
const BASE = {
  '1': '홈승', 'X': '무승부', 'x': '무승부', '2': '원정승',
  '1X': '홈 또는 무승부', '1x': '홈 또는 무승부', 'X2': '무승부 또는 원정', 'x2': '무승부 또는 원정',
  '12': '홈 또는 원정',
  'Odd': '홀', 'ODD': '홀', 'odd': '홀',
  'Even': '짝', 'EVEN': '짝', 'even': '짝',
  'Over': '오버', 'OVER': '오버', 'over': '오버',
  'Under': '언더', 'UNDER': '언더', 'under': '언더',
  'Yes': '예', 'yes': '예', 'YES': '예',
  'No': '아니오', 'no': '아니오', 'NO': '아니오',
  'Home': '홈', 'home': '홈',
  'Away': '원정', 'away': '원정',
  'Draw': '무승부', 'draw': '무승부',
};

// 명시 조합형 매핑 (우선 적용)
const COMBO_MAP = {
  'No Goal': '득점 없음',
  'no goal': '득점 없음',
  'Goal': '득점',
  'goal': '득점',
  'Home/Draw': '홈 또는 무승부',
  'Draw/Away': '무승부 또는 원정',
  'Home/Away': '홈 또는 원정',
};

const isMachineDisplayValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.startsWith('live365:')
    || normalized.startsWith('stable:live365:')
    || normalized.startsWith('catalog:')
    || normalized.includes(':selection_group:')
    || normalized.includes('selection_group:')
    || normalized.includes(':selection:')
    || normalized.includes(':scope:')
    || normalized.includes('contractmarketid')
    || normalized.includes('marketcontractid')
    || normalized.includes('selectioncontractid')
    || normalized.includes('contractselectionid')
    || normalized.includes('provideroddref')
    || /^key:.*::id:/i.test(normalized)
  );
};

const isPlaceholderDisplayValue = (value) => {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  return normalized === '-'
    || normalized === '–'
    || normalized === '—'
    || normalized === '선택지 정보 대기'
    || normalized === 'selection info pending';
};

const cleanDisplayValue = (value) => {
  const normalized = String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*\((?:none|null|undefined|nan)\)\s*$/i, '')
    .trim();
  if (!normalized || isMachineDisplayValue(normalized) || isPlaceholderDisplayValue(normalized)) return '';
  return normalized;
};

const isRangeLabelMarket = (marketName = '') => (
  /winning margin/i.test(marketName)
  || /\bmargin\b/i.test(marketName)
  || /승리\s*마진/i.test(marketName)
  || /승리\s*점수\s*차/i.test(marketName)
  || /점수\s*차/i.test(marketName)
);

const isRangeOrTimeNumericLabelMarket = (marketName = '') => {
  const market = String(marketName || '').trim().replace(/\s+/g, ' ');
  return /range|time|minute|interval|duration/i.test(market)
    || /범위|시간|구간|분/i.test(market);
};

const isRaceToMarket = (market = {}, marketName = '', odds = {}) => {
  const fields = odds?.v2_contract_fields || {};
  const display = odds?.v2_display_model || {};
  const sourceText = [
    marketName,
    market?.productGroup,
    market?.displayProductGroup,
    market?.v2_identity?.productGroup,
    market?.v2_identity?.displayProductGroup,
    market?.v2_display_model?.productGroup,
    market?.v2_display_model?.displayProductGroup,
    fields.productMarketFamily,
    fields.sourceMarketGroup,
    display.productGroup,
    display.displayProductGroup,
  ].filter(Boolean).join(' ');
  return /race[_\s-]?to|team[_\s-]?race[_\s-]?to|period[_\s-]?race[_\s-]?to|먼저\s*도달|팀\s*도달|팀도달|first\s+to/i.test(sourceText);
};

const readRaceToLineValue = (odds = {}, market = {}) => {
  const fields = odds?.v2_contract_fields || {};
  const display = odds?.v2_display_model || {};
  return [
    fields.neutralLineValue,
    fields.lineValue,
    fields.signedLine,
    fields.signedBaseLine,
    display.neutralLineValue,
    display.lineGroupDisplayName,
    odds.neutralLineValue,
    odds.lineValue,
    odds.signedLine,
    odds.display_line,
    odds.base_line,
    odds.line,
    market?._lineGroupDisplay,
    market?._baseLineLabel,
    market?.display_line,
    market?.base_line,
    market?.line,
  ].map(cleanDisplayValue).find(Boolean) || '';
};

const formatRaceToPointLine = (value) => {
  const clean = cleanDisplayValue(value);
  if (!clean) return '';
  if (/점$/.test(clean)) return clean;
  const numeric = clean.match(/^([+-]?\d+(?:\.\d+)?)\s*(?:points?|pts?)?$/i);
  if (numeric) return `${numeric[1].replace(/^\+/, '')}점`;
  return clean;
};

const stripRaceToLineFromLabel = (label = '', lineLabel = '') => {
  const normalized = normalizeToken(label);
  if (!normalized || !lineLabel) return normalized;
  const rawNumber = String(lineLabel).replace(/점$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return normalized
    .replace(new RegExp(`\\b${rawNumber}\\s*(?:points?|pts?|점)?\\b`, 'i'), '')
    .trim();
};

const appendRaceToLine = (label = '', lineValue = '') => {
  const cleanLabel = cleanDisplayValue(label);
  const lineLabel = formatRaceToPointLine(lineValue);
  if (!cleanLabel || !lineLabel) return cleanLabel;
  const stripped = stripRaceToLineFromLabel(cleanLabel, lineLabel);
  const normalizedBase = stripped || cleanLabel;
  const baseLower = normalizedBase.toLowerCase();
  const base = ['neither', 'none', 'no one', 'no team'].includes(baseLower) || ['없음', '해당 없음'].includes(normalizedBase)
    ? '없음'
    : normalizedBase;
  if (base.includes(lineLabel)) return base;
  return `${base} ${lineLabel}`;
};

const isAnyOtherResultSelection = (odds = {}, names = []) => {
  const fields = odds?.v2_contract_fields || {};
  const values = [
    fields.selectionOutcomeKind,
    fields.side,
    odds.selectionOutcomeKind,
    odds.outcomeKind,
    odds.side,
    ...names,
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
  return values.some((value) => (
    value === 'any_other_result'
    || value === 'any other result'
    || value === '기타 결과'
    || value === '그 외 결과'
    || value === '그 외 승차'
  ));
};

const isMeaningfulNumericSelectionLabel = (value, marketName = '') => {
  const clean = cleanDisplayValue(value);
  if (!clean) return false;
  const market = String(marketName || '').toLowerCase();
  if (/^\d+\s*-\s*\d+$/.test(clean)) {
    return /correct|score|exact|result|정확|스코어|점수|결과/.test(market)
      || isRangeLabelMarket(marketName)
      || isRangeOrTimeNumericLabelMarket(marketName);
  }
  if (/^\d+$/.test(clean)) {
    return /goal|goals|score|count|corner|card|point|골|득점|점수|개수|코너|카드|포인트/.test(market);
  }
  return true;
};

const cleanSelectionDisplayValue = (value, marketName = '') => {
  const clean = cleanDisplayValue(value);
  return clean && isMeaningfulNumericSelectionLabel(clean, marketName) ? clean : '';
};

const TEAM_MARKET_PATTERNS = [
  /승무패/i,
  /match result/i,
  /moneyline/i,
  /\b1x2\b/i,
  /\b12\b/i,
  /winner/i,
  /승패/i,
  /핸디캡/i,
  /handicap/i,
  /spread/i,
  /double chance/i,
  /더블찬스/i,
  /draw no bet/i,
  /무승부 환불/i,
  /first team to score/i,
  /last team to score/i,
  /next goal/i,
  /race to/i,
  /first to/i,
  /last to/i,
];

const WINNER_MARKET_PATTERNS = [
  /승무패/i,
  /match result/i,
  /moneyline/i,
  /\b1x2\b/i,
  /\b12\b/i,
  /winner/i,
  /승패/i,
];

const GOAL_COUNT_PATTERNS = [
  /number of goals?/i,
  /total goals?/i,
  /goal count/i,
  /정확한 득점수/i,
  /득점수/i,
  /골 수/i,
  /골수/i,
];

const normalizeToken = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const compactToken = (value) => normalizeToken(value).toLowerCase().replace(/[^a-z0-9가-힣]+/g, '');

const getMarketName = (market, marketName) => {
  if (marketName) return String(marketName);
  if (!market) return '';
  return String(
    market.marketDisplayNameKo
    || market.marketDisplayName
    || market.market_title_display_ko
    || market.market_title_display
    || market.name_ko
    || market.market_name_ko
    || market.market_type?.name_ko
    || market.market_type_name_ko
    || market.market_name
    || market.market_type_name
    || market.name
    || market.market_type?.name
    || market.market_code
    || ''
  );
};

const getTeamName = (match, side) => (
  match?.[`${side}_team_ko`]
  || match?.[`${side}_team`]
  || match?.[`${side}TeamKo`]
  || match?.[`${side}Team`]
  || ''
);

const isSameTeamToken = (value, teamName, teamNameKo) => {
  const target = compactToken(value);
  if (!target) return false;
  return [teamNameKo, teamName]
    .map((name) => compactToken(name))
    .filter(Boolean)
    .includes(target);
};

const isTeamNameMarket = (marketName) => {
  const name = normalizeToken(marketName);
  return Boolean(name) && TEAM_MARKET_PATTERNS.some((pattern) => pattern.test(name));
};

const isWinnerLabelMarket = (marketName) => {
  const name = normalizeToken(marketName);
  return Boolean(name) && WINNER_MARKET_PATTERNS.some((pattern) => pattern.test(name));
};

const isGoalCountMarket = (marketName) => {
  const name = normalizeToken(marketName);
  return Boolean(name) && GOAL_COUNT_PATTERNS.some((pattern) => pattern.test(name));
};

const getCountUnit = (marketName) => {
  const name = normalizeToken(marketName);
  if (/corner|코너/i.test(name)) return '코너';
  if (/card|booking|yellow|red|카드/i.test(name)) return '장';
  if (/goal|득점|골/i.test(name)) return '골';
  if (/point|score|점수/i.test(name)) return '점';
  return '개';
};

const getGenericTeamLabel = ({ side, marketName = '', fallback = '' } = {}) => {
  if (fallback) return fallback;

  if (side === 'draw') return '무승부';
  if (side === 'home_draw') return '홈 또는 무승부';
  if (side === 'draw_away') return '무승부 또는 원정';
  if (side === 'home_away') return '홈 또는 원정';

  const winnerLabel = isWinnerLabelMarket(marketName);
  if (side === 'home') return winnerLabel ? '홈승' : '홈';
  if (side === 'away') return winnerLabel ? '원정승' : '원정';
  return fallback || '';
};

const translateDirectToken = (name, options = {}) => {
  const {
    marketName = '',
    match,
    fallback = '',
    preferGenericTeamLabels = false,
  } = options;
  const token = normalizeToken(name);
  if (!token || isMachineDisplayValue(token)) return null;

  const home = getTeamName(match, 'home');
  const away = getTeamName(match, 'away');
  const normalized = token.toLowerCase();

  if (isSameTeamToken(token, match?.home_team, match?.home_team_ko)) {
    return preferGenericTeamLabels
      ? getGenericTeamLabel({ side: 'home', marketName, fallback })
      : home || token;
  }
  if (isSameTeamToken(token, match?.away_team, match?.away_team_ko)) {
    return preferGenericTeamLabels
      ? getGenericTeamLabel({ side: 'away', marketName, fallback })
      : away || token;
  }

  if (isGoalCountMarket(marketName)) {
    if (/^no goal$/i.test(token) || token === '무득점' || token === '득점 없음') return '무득점';
    if (/^\d+$/.test(token)) return `${token}${getCountUnit(marketName)}`;
    const orMoreMatch = token.match(/^(\d+)\s+or\s+more$/i);
    if (orMoreMatch) return `${orMoreMatch[1]}${getCountUnit(marketName)} 이상`;
  }

  if (isTeamNameMarket(marketName)) {
    if (['1', 'home', 'home win', 'home team', '홈', '홈승'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'home', marketName, fallback })
        : home || '홈';
    }
    if (['2', 'away', 'away win', 'away team', '원정', '원정승'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'away', marketName, fallback })
        : away || '원정';
    }
    if (['x', 'draw', '무', '무승부'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'draw', marketName, fallback })
        : '무승부';
    }
    if (['1x', '홈/무'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'home_draw', marketName, fallback })
        : `${home || '홈'} 또는 무승부`;
    }
    if (['x2', '무/원정'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'draw_away', marketName, fallback })
        : `무승부 또는 ${away || '원정'}`;
    }
    if (['12', '홈/원정'].includes(normalized)) {
      return preferGenericTeamLabels
        ? getGenericTeamLabel({ side: 'home_away', marketName, fallback })
        : `${home || '홈'} 또는 ${away || '원정'}`;
    }
  }

  if (['home', 'home team', '홈'].includes(normalized)) return '홈';
  if (['away', 'away team', '원정'].includes(normalized)) return '원정';
  if (['x', 'draw', '무', '무승부'].includes(normalized)) return '무승부';
  if (/^over\b/i.test(token)) return token.replace(/^over/i, '오버');
  if (/^under\b/i.test(token)) return token.replace(/^under/i, '언더');

  return BASE[token] || null;
};

// "A And B" 패턴 → "A + B" 번역
function translateAndCombo(name, options = {}) {
  const parts = name.split(/\s+[Aa]nd\s+/);
  if (parts.length !== 2) return null;
  const left = translateDirectToken(parts[0], options) || parts[0].trim();
  const right = translateDirectToken(parts[1], options) || parts[1].trim();
  return `${left} + ${right}`;
}

// "A/B" 패턴 → "A/B" 번역
function translateSlashCombo(name, options = {}) {
  if (name.includes('/') && !BASE[name]) {
    const parts = name.split('/');
    if (parts.length === 2) {
      const left = translateDirectToken(parts[0], options) || parts[0].trim();
      const right = translateDirectToken(parts[1], options) || parts[1].trim();
      return `${left}/${right}`;
    }
  }
  return null;
}

export function getSelectionLabel(odds, context = {}) {
  if (!odds) return cleanDisplayValue(context.fallback) || '-';

  const names = [
    cleanDisplayValue(odds.selectionDisplayName),
    cleanDisplayValue(odds.selection_display_label),
    cleanDisplayValue(odds.selection_name_display),
    cleanDisplayValue(odds.selection_name_display_ko),
    cleanDisplayValue(odds.name_ko),
    cleanDisplayValue(odds.name),
    cleanDisplayValue(odds.displayLabel),
    cleanDisplayValue(odds.selectionLabel),
  ].filter(Boolean);
  const uniqueNames = [...new Set(names)];
  const marketName = getMarketName(context.market, context.marketName);
  const raceToMarket = isRaceToMarket(context.market, marketName, odds);
  const raceToLine = raceToMarket ? readRaceToLineValue(odds, context.market) : '';

  if (isRangeLabelMarket(marketName) && isAnyOtherResultSelection(odds, uniqueNames)) {
    return '그 외 결과';
  }

  const finalizeLabel = (label) => (
    raceToMarket ? appendRaceToLine(label, raceToLine) : label
  );

  for (const name of uniqueNames) {
    const directResult = translateDirectToken(name, { ...context, marketName });
    if (directResult) return finalizeLabel(directResult);

    const andResult = translateAndCombo(name, { ...context, marketName });
    if (andResult) return finalizeLabel(andResult);

    const slashResult = translateSlashCombo(name, { ...context, marketName });
    if (slashResult) return finalizeLabel(slashResult);

    const lower = name.toLowerCase();
    for (const [key, val] of Object.entries(COMBO_MAP)) {
      if (key.toLowerCase() === lower) return finalizeLabel(val);
    }

    const displayValue = cleanSelectionDisplayValue(name, marketName);
    if (displayValue) return finalizeLabel(displayValue);
  }

  if (cleanSelectionDisplayValue(odds.name_ko, marketName)) return finalizeLabel(cleanSelectionDisplayValue(odds.name_ko, marketName));
  if (cleanSelectionDisplayValue(odds.name, marketName)) return finalizeLabel(cleanSelectionDisplayValue(odds.name, marketName));
  const fallback = cleanSelectionDisplayValue(context.fallback, marketName);
  return fallback ? finalizeLabel(fallback) : '-';
}

export default getSelectionLabel;
